import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { api } from '@/api/client';
import type { MessengerMessage, MessengerThreadMemberRow } from '@/api/client';
import { DirectThreadView } from '@/components/Messenger/direct/DirectThreadView';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { ChevronRightIcon } from '@/components/ui/Icons';
import { CreateGroupChatModal } from '@/components/Messenger/group/CreateGroupChatModal';
import type { ChatContactUser } from '@/components/Messenger/sidebar/ChatSidebar';
import { MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY } from '@/constants/messengerSearchHighlight';
import './GroupThreadView.css';

/** display_name с API: first_name + ' ' + last_name — фамилия совпадает с последним словом */
function messengerMemberSurnameKey(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts[parts.length - 1] ?? '';
}

function compareMessengerMembersByAdminThenSurname(
  a: MessengerThreadMemberRow,
  b: MessengerThreadMemberRow
): number {
  const aAdm = a.member_role === 'admin' ? 0 : 1;
  const bAdm = b.member_role === 'admin' ? 0 : 1;
  if (aAdm !== bAdm) return aAdm - bAdm;
  const bySurname = messengerMemberSurnameKey(a.display_name).localeCompare(
    messengerMemberSurnameKey(b.display_name),
    'ru',
    { sensitivity: 'base' }
  );
  if (bySurname !== 0) return bySurname;
  return a.display_name.localeCompare(b.display_name, 'ru', { sensitivity: 'base' });
}

export interface GroupThreadViewProps {
  threadId: string;
  title: string;
  myMemberRole: 'admin' | 'member';
  fabUnreadCount: number;
  onClose?: () => void;
  readReceiptsActive?: boolean;
  messengerSurfaceVisible?: boolean;
  onVisibleIncomingPeerMessages?: (count: number) => void;
  onForwardMessage?: (msg: MessengerMessage) => void;
  onForwardMessages?: (msgs: MessengerMessage[]) => void;
  initialForwardMsgs?: MessengerMessage[];
  onConsumePendingForwardDraft?: () => void;
  forwardSelectionClearSignal?: { nonce: number; threadId: string } | null;
  onThreadMessageCommitted?: (msg: MessengerMessage) => void;
  jumpToMessage?: { threadId: string; messageId: string; nonce: number } | null;
  onJumpToMessageSettled?: (nonce: number) => void;
  users: ChatContactUser[];
  onThreadsRefresh: () => void | Promise<void>;
  onGroupRemoved: (threadId: string) => void;
  /** Слова строки поиска сайдбара — подсветка текста в пузырях */
  sidebarSearchHighlightWords?: readonly string[];
}

export function GroupThreadView({
  threadId,
  title,
  myMemberRole,
  fabUnreadCount,
  onClose,
  readReceiptsActive = false,
  messengerSurfaceVisible = true,
  onVisibleIncomingPeerMessages,
  onForwardMessage,
  onForwardMessages,
  initialForwardMsgs,
  onConsumePendingForwardDraft,
  forwardSelectionClearSignal,
  onThreadMessageCommitted,
  jumpToMessage,
  onJumpToMessageSettled,
  users,
  onThreadsRefresh,
  onGroupRemoved,
  sidebarSearchHighlightWords = MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY,
}: GroupThreadViewProps) {
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<{ x: number; y: number } | null>(null);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);

  const [members, setMembers] = useState<MessengerThreadMemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [membersSubmenuAnchor, setMembersSubmenuAnchor] = useState<HTMLElement | null>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchor(null);
    setMembersSubmenuAnchor(null);
  }, []);

  const cancelSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    submenuCloseTimerRef.current = setTimeout(() => {
      setMembersSubmenuAnchor(null);
      submenuCloseTimerRef.current = null;
    }, 150);
  }, []);

  useEffect(() => {
    return () => {
      if (submenuCloseTimerRef.current) {
        clearTimeout(submenuCloseTimerRef.current);
        submenuCloseTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setMembersLoading(true);
    setMembersError(null);
    void api.messenger
      .getThreadMembers(threadId)
      .then((r) => {
        if (!cancelled) {
          setMembers(r.members);
          setMembersLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setMembersError((e as Error).message);
          setMembers([]);
          setMembersLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  const groupMemberDisplayById = useMemo(() => {
    const o: Record<string, string> = {};
    for (const m of members) {
      o[m.person_id] = m.display_name;
    }
    return o;
  }, [members]);

  const openMenuFromButton = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setMenuAnchor({ x: r.left, y: r.bottom + 4 });
    setMenuOpen(true);
  }, []);

  const handleLeave = useCallback(async () => {
    if (!window.confirm('Покинуть группу?')) return;
    closeMenu();
    try {
      const res = await api.messenger.leaveThread(threadId);
      await onThreadsRefresh();
      if (res.thread_deleted) {
        onGroupRemoved(threadId);
      }
    } catch {
      /* ignore */
    }
  }, [threadId, onThreadsRefresh, onGroupRemoved, closeMenu]);

  const handleDeleteGroup = useCallback(async () => {
    if (!window.confirm('Удалить группу безвозвратно? Все сообщения будут удалены.')) return;
    closeMenu();
    try {
      await api.messenger.deleteGroupThread(threadId);
      await onThreadsRefresh();
      onGroupRemoved(threadId);
    } catch {
      /* ignore */
    }
  }, [threadId, onThreadsRefresh, onGroupRemoved, closeMenu]);

  const reloadMembers = useCallback(async () => {
    try {
      const r = await api.messenger.getThreadMembers(threadId);
      setMembers(r.members);
      setMembersError(null);
    } catch (e) {
      setMembersError((e as Error).message);
    }
  }, [threadId]);

  const handleRemoveMember = useCallback(
    async (personId: string) => {
      if (!window.confirm('Исключить участника из группы?')) return;
      setRemovingId(personId);
      try {
        await api.messenger.removeThreadMember(threadId, personId);
        await onThreadsRefresh();
        await reloadMembers();
      } catch {
        /* ignore */
      } finally {
        setRemovingId(null);
      }
    },
    [threadId, onThreadsRefresh, reloadMembers]
  );

  const sortedMembers = useMemo(
    () => [...members].sort(compareMessengerMembersByAdminThenSurname),
    [members]
  );

  const groupHeaderMenu = useMemo(
    () => (
    <>
      <button
        type="button"
        className="groupThreadView__menuBtn"
        aria-label="Меню группы"
        aria-expanded={menuOpen}
        onClick={openMenuFromButton}
      >
        <i className="fa-solid fa-ellipsis-vertical" aria-hidden />
      </button>
      <ContextMenu isOpen={menuOpen} onClose={closeMenu} anchorPosition={menuAnchor}>
        <ul className="context-menu__list">
          <li
            className="context-menu__list-item groupThreadView__menuItem_withSub"
            onMouseEnter={(e) => {
              cancelSubmenuClose();
              setMembersSubmenuAnchor(e.currentTarget);
            }}
            onMouseLeave={() => scheduleSubmenuClose()}
          >
            <button type="button" className="context-menu__action context-menu__action_with-sub">
              <span className="context-menu__action-text">Участники</span>
              <ChevronRightIcon size={16} className="context-menu__action-chevron" />
            </button>
            {membersSubmenuAnchor ? (
              <ContextMenuSubmenu
                anchorEl={membersSubmenuAnchor}
                onMouseEnter={cancelSubmenuClose}
                onMouseLeave={() => {
                  setMembersSubmenuAnchor(null);
                }}
              >
                <ul className="context-menu__list groupThreadView__membersSublist" role="list">
                  {membersLoading ? (
                    <li className="context-menu__list-item">
                      <span className="groupThreadView__membersSublistStatus">Загрузка…</span>
                    </li>
                  ) : membersError ? (
                    <li className="context-menu__list-item">
                      <span className="groupThreadView__membersSublistError">{membersError}</span>
                    </li>
                  ) : sortedMembers.length === 0 ? (
                    <li className="context-menu__list-item">
                      <span className="groupThreadView__membersSublistStatus">Нет участников</span>
                    </li>
                  ) : (
                    sortedMembers.map((m) => {
                      const isSelf = m.person_id === currentUserId;
                      const canKick =
                        myMemberRole === 'admin' && m.member_role !== 'admin' && !isSelf;
                      return (
                        <li key={m.person_id} className="context-menu__list-item" role="listitem">
                          <div className="groupThreadView__memberRow">
                            <span className="groupThreadView__memberRowMain">
                              <span className="groupThreadView__memberRowName">{m.display_name}</span>
                              {m.member_role === 'admin' ? (
                                <span className="groupThreadView__memberRowBadge">админ</span>
                              ) : null}
                            </span>
                            {canKick ? (
                              <button
                                type="button"
                                className="groupThreadView__memberKick"
                                disabled={removingId === m.person_id}
                                aria-label="Исключить из группы"
                                title="Исключить"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleRemoveMember(m.person_id);
                                }}
                              >
                                {removingId === m.person_id ? (
                                  <span className="groupThreadView__memberKickSpinner" aria-hidden />
                                ) : (
                                  <i className="fa-solid fa-user-minus" aria-hidden />
                                )}
                              </button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })
                  )}
                </ul>
              </ContextMenuSubmenu>
            ) : null}
          </li>
          {myMemberRole === 'admin' ? (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__action"
                onClick={() => {
                  closeMenu();
                  setAddMemberModalOpen(true);
                }}
              >
                <span className="context-menu__action-text">Добавить участника</span>
              </button>
            </li>
          ) : null}
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action"
              onClick={() => {
                void handleLeave();
              }}
            >
              <span className="context-menu__action-text">Покинуть группу</span>
            </button>
          </li>
          {myMemberRole === 'admin' ? (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__action context-menu__action_danger"
                onClick={() => {
                  void handleDeleteGroup();
                }}
              >
                <span className="context-menu__action-text">Удалить группу</span>
              </button>
            </li>
          ) : null}
        </ul>
      </ContextMenu>
    </>
    ),
    [
      menuOpen,
      menuAnchor,
      membersSubmenuAnchor,
      membersLoading,
      membersError,
      sortedMembers,
      myMemberRole,
      removingId,
      currentUserId,
      openMenuFromButton,
      closeMenu,
      cancelSubmenuClose,
      scheduleSubmenuClose,
      handleRemoveMember,
      handleLeave,
      handleDeleteGroup,
    ]
  );

  return (
    <>
      <DirectThreadView
        variant="group"
        threadId={threadId}
        peerId=""
        peerName={title}
        fabUnreadCount={fabUnreadCount}
        onClose={onClose}
        readReceiptsActive={readReceiptsActive}
        messengerSurfaceVisible={messengerSurfaceVisible}
        onVisibleIncomingPeerMessages={onVisibleIncomingPeerMessages}
        onForwardMessage={onForwardMessage}
        onForwardMessages={onForwardMessages}
        initialForwardMsgs={initialForwardMsgs}
        onConsumePendingForwardDraft={onConsumePendingForwardDraft}
        forwardSelectionClearSignal={forwardSelectionClearSignal}
        onThreadMessageCommitted={onThreadMessageCommitted}
        jumpToMessage={jumpToMessage}
        onJumpToMessageSettled={onJumpToMessageSettled}
        groupHeaderMenu={groupHeaderMenu}
        groupMemberDisplayById={groupMemberDisplayById}
        sidebarSearchHighlightWords={sidebarSearchHighlightWords}
      />
      <CreateGroupChatModal
        variant="addMembers"
        open={addMemberModalOpen}
        threadId={threadId}
        users={users}
        currentUserId={currentUserId}
        onClose={() => setAddMemberModalOpen(false)}
        onMembersAdded={onThreadsRefresh}
        onThreadsRefresh={onThreadsRefresh}
      />
    </>
  );
}
